/* angular/components/sharpr/google/googleDrive.js and a-react-app/src/libs/google/googleDrive.js should be identical and kept in sync.
	Run `node a-react-app/scripts/copy-other.js` to copy the angular version to the react directory. */

import dayjs from 'dayjs';

export class GoogleDrive {
	constructor() {
		this.apiBaseUrl = 'https://www.googleapis.com/drive/v3/files';
		this.gdriveExtensionsRegex = /\.g(slides|sheet|doc|draw|form|map|site|jam)$/;
	}

	/**
	 * Google Drive files like Docs and Slides exist in the Google Drive app as JSON files with information
	 * about the file. This function checks if the file contains parsable JSON with the expected keys.
	 * NOTE: Google Drive files are only detectable this way if files are set to "Mirror" in the gdrive desktop app preferences.
	 * I found no way to read the file if files are set to "Stream" instead of "Mirror".
	 * @param file Object  A file object as provided by plupload
	 * @returns {Promise<{is_gdrive: boolean, gdrive_id: null, gdrive_url: null, gdrive_name: null, modified: null}>}
	 */
	async isGoogleDriveFile(file) {
		console.debug('checking if gdrive', JSON.stringify(file));
		const result = {
			is_gdrive: false,
			gdrive_id: null,
			gdrive_url: null,
			gdrive_name: null,
			modified: null,
		};
		try {
			// this branch is for "mirrored" gdrive files. If anything throws here, the `catch` will go into the process
			/// of testing for a "synced" gdrive file. (see https://support.google.com/drive/answer/10838124?hl=en)
			const fileText = await file._nativeFile.text();
			const contents = JSON.parse(fileText);
			const keys = Object.keys(contents);

			console.debug('json contents in isGoogleDriveFile', contents);

			if (keys.indexOf('doc_id') > -1 && keys.indexOf('url') > -1 && contents.url.startsWith('https://docs.google.com')) {
				result.is_gdrive = true;
				result.gdrive_id = contents.doc_id;
				result.gdrive_url = contents.url;
			}

			// the file has all info we need to download it from gdrive
			return result;
		} catch (e) {
			// if something failed above, this may be a synced file.
			console.debug('Error trying to read the file or parse the JSON. Error:', e.name);
			if (e.name === 'AbortError' || e.name === 'NotReadableError') {
				// This is the exception thrown when the user drags a file from a *shared* Google Drive. Test the extension to make sure it's a google file.
				const hasGoogleDriveExtension = file.name.match(this.gdriveExtensionsRegex);
				if (hasGoogleDriveExtension) {
					result.is_gdrive = true;
					result.gdrive_name = file.rawName;
					result.modified = file.lastModified;
				}
				return result;
			} else {
				// Probably failed to parse JSON (meaning it's not a mirrored gdrive file)
				return result;
			}
		}
	}

	/**
	 * This determines if a file should be downloaded with the `/drive/v3/files` or `/drive/v3/files/$id/export` endpoint
	 * @param mimeType
	 * @returns {boolean}
	 */
	fileNeedsExport(mimeType) {
		return /^application\/vnd.google-apps\.(?!folder)/.test(mimeType);
	}

	/**
	 * Request information about the file from the GoogleDrive API
	 *
	 * @param fileId String  The id of the file on Google Drive
	 * @param token an authorization like the one returned from the OAuth process
	 * @returns {Promise<any>} if the response contains the key `error`, the call failed. Check `error.code` and `error.message`.
	 * Otherwise, returns a google drive file resource (https://developers.google.com/drive/api/v3/reference/files).
	 * Only driveId, id, kind, mimeType, name, and teamDriveId are included by default. See the `fields` param for this
	 * endpoint to include others in the response).
	 */
	async getFileDetails(fileId, token) {
		// supportsAllDrives makes it also look in shared drives
		const url = `${this.apiBaseUrl}/${fileId}?supportsAllDrives=true&fields=id,kind,mimeType,name,exportLinks,webViewLink`;
		console.debug(`Going to request file details from '${url}'.`);

		const response = await fetch(url, {
			headers: { Authorization: `Bearer ${token}` },
		});

		return await response.json();
	}

	/**
	 * Use Google Drive API search to find a file by name.
	 * @see https://developers.google.com/drive/api/guides/search-files
	 * @param modified Number  a unix timestamp in milliseconds representing the last time the file was modified. This
	 * time is relative to the user's system. It is the time reported by the user's filesystem on upload.
	 * @param gdrive_name String  the name of the file on Google Drive (with or without extension)
	 * @param token String  The auth token to use for connecting to Google Drive
	 * @returns {Promise<Object>}
	 */
	async searchForFile({ modified, gdrive_name }, token) {
		console.debug('original modified time of dragged file', modified);
		console.debug('name of file to search for', gdrive_name);
		// giving a one-second buffer
		const modifiedMinusBuffer = modified - 1000;
		// ISO8601 format including the timezone offset hours
		const modifiedDateTime = dayjs(modifiedMinusBuffer).format();
		// the file won't be found if we search with the extension
		const nameWithoutExtension = gdrive_name.replace(this.gdriveExtensionsRegex, '');
		// docs use single quotes around `name` and `modifiedTime` values. This is using double quotes since
		/// values can have apostrophes in them. It doesn't seem to affect the outcome from gdrive API.
		const query = `name="${nameWithoutExtension}" and modifiedTime > "${modifiedDateTime}"`;
		const searchParams = {
			supportsAllDrives: true,
			corpora: 'allDrives',
			includeItemsFromAllDrives: true,
			// query needs to be url encoded, but URLSearchParams handles it
			q: query,
			fields: 'files(id,trashed,modifiedTime)',
		};
		const search = new URLSearchParams(searchParams);

		const url = `${this.apiBaseUrl}?${search.toString()}`;
		console.debug('file search URL: ', url);
		const response = await fetch(url, {
			headers: { Authorization: `Bearer ${token}` },
		});

		console.debug('file search response', response);

		return await response.json();
	}

	/**
	 * Takes the gdrive mimeType and translates to the equivalent MS Office type (or defaults to PDF)
	 * @see https://developers.google.com/drive/api/v3/ref-export-formats
	 * @param mimeType
	 * @returns Object {extension: string, mimeType: string}
	 */
	getEquivalentMimeTypeAndExtension(mimeType) {
		switch (mimeType) {
			case 'application/vnd.google-apps.document':
				// Google Doc
				return {
					mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
					extension: 'docx',
				};
			case 'application/vnd.google-apps.spreadsheet':
				// Google Sheet
				return {
					mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
					extension: 'xlsx',
				};
			case 'application/vnd.google-apps.presentation':
				// Google Slides
				return {
					mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
					extension: 'pptx',
				};
			default:
				// any other Google file
				return { mimeType: 'application/pdf', extension: 'pdf' };
		}
	}

	/**
	 * Use the GoogleDrive API to download or export a file
	 * @see GoogleDrive::download (app/libs/PostSync/GoogleDrive.php)
	 * @param id String  the id of the file on Google Drive
	 * @param mimeType String
	 * @param token String  a bearer token to use for API authentication
	 * @returns {Promise}
	 */
	async downloadFile({ id, mimeType, exportLinks }, token) {
		let newExtension = undefined;
		// `?alt=media` tells gdrive API to send the file contents. This is the default we want
		/// unless we need to export.
		let downloadUrl = `${this.apiBaseUrl}/${id}?alt=media`;

		if (this.fileNeedsExport(mimeType)) {
			const exportDetails = this.getEquivalentMimeTypeAndExtension(mimeType);
			newExtension = exportDetails.extension;
			// google provides a URL to download the file with the requested mime type.
			downloadUrl = exportLinks[exportDetails.mimeType];
		}

		const response = await fetch(downloadUrl, {
			headers: { Authorization: `Bearer ${token}` },
		});

		response.newExtension = newExtension || undefined;

		return response;
	}

	/**
	 * Iterates through `files`. On each iteration, calculates the difference between `uploadFileModified` and the
	 * item's own modified time. Returns the `files` item with the smallest difference between the two.
	 * @param uploadedFileModified Number  A unix timestamp representing the day and time the dragged file was last modified.
	 * @param files Object[]  An array of files
	 * @property id String  The file's id on Google Drive
	 * @property trashed Boolean  Whether the file is in the Google Drive trash. These files are never chosen.
	 * @property modifiedTime Number  A unix timestamp representing the day and time the file was last modified.
	 *
	 * @returns Object  The item in `files` with the closest time to `uploadedFileModified`
	 */
	findClosestModifiedTime(uploadedFileModified, files) {
		let bestCandidate = null;
		files.forEach(file => {
			console.debug('checking this file modified time for closest match', file);
			if (file.trashed === true) {
				// You can't open the trash on Google Drive Desktop, so a trashed file should never be the correct one.
				return;
			}
			const fileModified = dayjs(file.modifiedTime).valueOf();
			console.debug('Math.abs(fileModified - uploadedFileModified)', Math.abs(fileModified - uploadedFileModified));
			// get the difference between this file's modified time and the original upload's modified time.
			/// Then compare with the diff in the current best candidate file.
			file.modifiedDiff = Math.abs(fileModified - uploadedFileModified);
			if (bestCandidate === null || file.modifiedDiff < bestCandidate.modifiedDiff) {
				bestCandidate = file;
			}
		});

		return bestCandidate;
	}

	/**
	 * Plupload requires a native browser file object. This function downloads the file and turns the binary into a native file.
	 * @param googleFile
	 * @property gdrive_id String (optional)  the id of the file on Google Drive. If not given, the file id will be found on Google Drive with a file name search.
	 * @property gdrive_url String (optional)  Will be requested from Google Drive API if the file id is not given.
	 * @property gdrive_name String (optional)  only required if the file id is not provided.
	 * @property modified String|Number (optional)  A date time parseable by dayjs. Will be used to narrow down results when searching by name for the file on Google Drive.
	 * @param token
	 * @returns {Promise<{success: boolean, message: string}|{success: boolean, nativeFile: *}>}
	 */
	async getNativeFile(googleFile, token) {
		let fileId = googleFile.gdrive_id;
		if (fileId === null) {
			// we only have the file name. Search for the file on google to get the file id
			const fileSearchResult = await this.searchForFile(googleFile, token);
			console.debug('fileSearchResult', fileSearchResult);
			if (fileSearchResult.files && fileSearchResult.files.length) {
				if (fileSearchResult.files.length === 1) {
					fileId = fileSearchResult.files[0].id;
				} else {
					const closestFile = this.findClosestModifiedTime(googleFile.modified, fileSearchResult.files);
					if (closestFile === null) {
						return {
							success: false,
							message: `Unable to find the file ${googleFile.gdrive_name}.`,
						};
					}
					console.debug('Searched through all files to find the closest to the provided modified date', closestFile);
					fileId = closestFile.id;
				}
			} else {
				let message = `Unable to find the file ${googleFile.gdrive_name}.`;
				if (
					fileSearchResult.error &&
					fileSearchResult.error.errors &&
					fileSearchResult.error.errors.length > 0 &&
					fileSearchResult.error.errors[0].message
				) {
					message += ` Message from Google Drive: ${fileSearchResult.error.errors[0].message}.`;
				}
				return {
					success: false,
					message,
				};
			}
		}
		const fileResponse = await this.getFileDetails(fileId, token);
		console.debug('fileResponse', fileResponse);
		if (fileResponse.error) {
			console.error('Could not get file details: ', fileResponse.error.errors);
			return {
				success: false,
				message: 'Could not get file details. Message from Google Drive: `' + fileResponse.error.errors[0].message + '`',
			};
		}

		const downloadResponse = await this.downloadFile(fileResponse, token);
		console.debug('downloadResponse', downloadResponse);
		if (downloadResponse.ok === false) {
			const body = await downloadResponse.json();
			console.error('Could not download file: ', downloadResponse, body);
			const errorMessage = `${downloadResponse.statusText}: ${body.error && body.error.message ? body.error.message : ''}`;
			return {
				success: false,
				message: 'Could not download file. Message from Google Drive: `' + errorMessage + ' `',
			};
		}
		// google file (docs, slides, etc) names don't have an extension, but file processing relies on the
		/// extension to decide which paths to send the file through.
		if (downloadResponse.newExtension) {
			fileResponse.name += '.' + downloadResponse.newExtension;
		}

		// data needs to be a native browser File object so we can send it through the plupload process
		const blob = await downloadResponse.blob();
		const nativeFile = new File([blob], fileResponse.name, {
			type: blob.type,
		});

		// this gets saved to attachments.external_ref. Unfortunately, it only works if it's a native google file like google doc.
		/// We can't detect if they uploaded a regular file (like a PDF) from the Google Drive app. Those files don't
		/// get external_ref values.
		nativeFile.externalRef = `GoogleDrive:${fileId}`;
		if (googleFile.gdrive_url === null && Object.keys(fileResponse).includes('webViewLink')) {
			// if we had to search for the file, we don't have the url yet.
			nativeFile.gdrive_url = fileResponse.webViewLink;
		}

		return { success: true, nativeFile };
	}
}

export const googleDrive = new GoogleDrive();
